/**
 * OAuth utilities for centralized authentication
 */

/**
 * Generate a random state parameter for OAuth CSRF protection
 */
export function generateOAuthState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Generate OAuth authorization URL for "Login with Texture"
 * @param origin - The origin URL for the redirect URI (required for SSR compatibility)
 */
export function getOAuthLoginUrl(origin?: string, redirectUri?: string): string {
  const authUrl = process.env.NEXT_PUBLIC_AUTH_PORTAL_URL || "http://localhost:4000";
  const clientId = process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID;

  if (!clientId) {
    throw new Error("NEXT_PUBLIC_OAUTH_CLIENT_ID environment variable is required");
  }

  let callbackUri = redirectUri;
  if (!callbackUri) {
    if (origin) {
      callbackUri = `${origin}/auth/callback`;
    } else if (typeof window !== "undefined") {
      callbackUri = `${window.location.origin}/auth/callback`;
    } else {
      throw new Error("Origin must be provided for server-side rendering");
    }
  }

  const existing = typeof window !== "undefined" ? sessionStorage.getItem("oauth_state") : null;
  const state = existing ?? generateOAuthState();
  if (typeof window !== "undefined") {
    sessionStorage.setItem("oauth_state", state);
    sessionStorage.setItem("org_switch_state", state);
  }

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: callbackUri,
    scope: "openid profile email offline_access graph.read graph.write",
    state,
    response_type: "code",
  });

  return `${authUrl}/login?${params.toString()}`;
}

/**
 * Validate OAuth state parameter for CSRF protection
 */
export function validateOAuthState(state: string): boolean {
  const storedState = sessionStorage.getItem("oauth_state");

  // If no stored state or mismatch, validation fails
  if (!storedState || storedState !== state) {
    // Don't clear state on failure - it may be needed for retry
    return false;
  }

  // Only clear state after successful validation to prevent reuse
  sessionStorage.removeItem("oauth_state");
  return true;
}

/**
 * Generate OAuth logout URL for centralized OIDC logout
 * This will revoke refresh tokens and clear SSO sessions server-side
 * @param origin - The origin URL for the redirect URI (required for SSR compatibility)
 */
export function getOAuthLogoutUrl(origin?: string): string {
  const authUrl = process.env.NEXT_PUBLIC_AUTH_PORTAL_URL || "http://localhost:4000";
  const clientId = process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID;

  if (!clientId) {
    throw new Error("NEXT_PUBLIC_OAUTH_CLIENT_ID environment variable is required");
  }

  let postLogoutRedirectUri: string;
  if (origin) {
    postLogoutRedirectUri = `${origin}/signin`;
  } else if (typeof window !== "undefined") {
    postLogoutRedirectUri = `${window.location.origin}/signin`;
  } else {
    throw new Error("Origin must be provided for server-side rendering");
  }

  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri,
    client_id: clientId,
  });

  return `${authUrl}/oauth/logout?${params.toString()}`;
}
